/**
 * useCachedData - Shows cached data instantly instead of loading spinner.
 *
 * On first visit: shows loading spinner (normal behavior)
 * On revisit (within 5 min): shows cached data instantly, fetches fresh in background
 * After mutations: cache is auto-invalidated, next visit fetches fresh data
 *
 * Usage:
 *   const { data: goal, setData: setGoal, loading, setLoading } =
 *     useCachedData<Goal>(`goal-${id}`);
 */

import { useState, useCallback, useRef } from 'react';
import { getCachedData, setCachedData } from '../utils/dataCache';

export function useCachedData<T>(cacheKey: string) {
  const cached = useRef(getCachedData<T>(cacheKey)).current;
  const [data, setDataRaw] = useState<T | null>(cached);
  const [loading, setLoading] = useState(!cached);

  const setData = useCallback((newData: T | null) => {
    setDataRaw(newData);
    if (newData !== null && cacheKey) {
      setCachedData(cacheKey, newData);
    }
  }, [cacheKey]);

  return { data, setData, loading, setLoading };
}
